/**
 * Browser-side counterpart of the host's audio-publisher. Handles
 * SDP + ICE through the control WS and attaches the incoming audio
 * track to an <audio> element so the viewer hears host system audio
 * alongside the H.264 video.
 *
 * Phase 1: audio-only, receive-only, Opus. Host is the offerer, viewer
 * answers. STUN only (Google's public server) for ICE gathering.
 *
 * Chrome-only in Phase 1 by design: we use the modern WebRTC API
 * surface. Multi-browser support comes with later phases if needed.
 */

import type { ControlWs } from './control-ws';

export interface WebRtcAudioSubscriberOpts {
  sessionId: number;
  control: ControlWs;
  audioEl: HTMLAudioElement;
  iceServers?: RTCIceServer[];
  onStateChange?: (state: RTCPeerConnectionState) => void;
  onError?: (err: Error) => void;
}

export interface WebRtcAudioSubscriber {
  // Called from the ControlWs handler when a WEBRTC_OFFER arrives.
  handleOffer: (sdp: string) => Promise<void>;
  // Called from the ControlWs handler when a WEBRTC_ICE arrives.
  handleIce: (c: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null }) => Promise<void>;
  // Mute / unmute locally. True = audible, false = silent (track still
  // flows over the wire; we do not renegotiate for a mute toggle).
  setEnabled: (enabled: boolean) => void;
  // Current playback state - undefined until the first ontrack.
  isEnabled: () => boolean;
  close: () => void;
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export function createWebRtcAudioSubscriber(opts: WebRtcAudioSubscriberOpts): WebRtcAudioSubscriber {
  const pc = new RTCPeerConnection({ iceServers: opts.iceServers ?? DEFAULT_ICE_SERVERS });

  let audioTrack: MediaStreamTrack | null = null;
  let enabled = true;

  pc.addEventListener('track', (ev) => {
    // The host adds a sendonly audio transceiver. We attach the first
    // audio track that arrives and ignore anything else (video will come
    // in later phases on separate transceivers).
    if (ev.track.kind !== 'audio') return;
    audioTrack = ev.track;
    audioTrack.enabled = enabled;
    const stream = ev.streams[0] ?? new MediaStream([ev.track]);
    opts.audioEl.srcObject = stream;
    // Autoplay policy: audio-only MediaStream usually plays without a
    // user gesture, but defend with a .play() in case the browser needs
    // the kick. The viewer navigated to this page via a user action
    // (clicking the host card) so the gesture requirement is satisfied
    // for this origin.
    const p = opts.audioEl.play();
    if (p && typeof p.then === 'function') {
      p.catch((err: Error) => opts.onError?.(err));
    }
  });

  pc.addEventListener('icecandidate', (ev) => {
    // Trickle every candidate (including the null end-of-candidates
    // marker) back to the host via the control WS. The relay forwards
    // verbatim.
    try {
      if (ev.candidate) {
        opts.control.sendWebRtcIce(opts.sessionId, {
          candidate: ev.candidate.candidate,
          sdpMid: ev.candidate.sdpMid,
          sdpMLineIndex: ev.candidate.sdpMLineIndex,
        });
      } else {
        opts.control.sendWebRtcIce(opts.sessionId, { candidate: '', sdpMid: null, sdpMLineIndex: null });
      }
    } catch { /* control WS churn */ }
  });

  pc.addEventListener('connectionstatechange', () => {
    opts.onStateChange?.(pc.connectionState);
  });

  async function handleOffer(sdp: string): Promise<void> {
    try {
      await pc.setRemoteDescription({ type: 'offer', sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      if (answer.sdp) {
        opts.control.sendWebRtcAnswer(opts.sessionId, answer.sdp);
      }
    } catch (err) {
      opts.onError?.(err as Error);
    }
  }

  async function handleIce(c: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null }): Promise<void> {
    try {
      // Empty candidate string = end-of-candidates. Pass null to the
      // browser so it marks ICE gathering complete.
      if (c.candidate === '') {
        await pc.addIceCandidate(null as unknown as RTCIceCandidateInit);
        return;
      }
      await pc.addIceCandidate({
        candidate: c.candidate,
        sdpMid: c.sdpMid,
        sdpMLineIndex: c.sdpMLineIndex,
      });
    } catch (err) {
      opts.onError?.(err as Error);
    }
  }

  function setEnabled(v: boolean): void {
    enabled = v;
    if (audioTrack) audioTrack.enabled = v;
    opts.audioEl.muted = !v;
  }

  function isEnabled(): boolean {
    return enabled;
  }

  function close(): void {
    try { opts.audioEl.srcObject = null; } catch { /* ignore */ }
    try { pc.close(); } catch { /* ignore */ }
  }

  return { handleOffer, handleIce, setEnabled, isEnabled, close };
}
